import { useState } from 'react';
import { ALL_LANG_CODES, I18n, LANG_LABELS, sortLangKeys } from '../utils/i18n';
import { confirmDialog } from '../utils/dialog';
import SearchSelect from './SearchSelect';

// 多语言 map 编辑器: 逐语种一行 [语种标签][文本输入][移除], 底部可追加语种.
// zh/en 固定展示不可移除 (对齐 xcs-api: 创建只强制 zh/en, 其余语种渐进补齐).
interface I18nEditorProps {
    value: I18n;
    onChange: (next: I18n) => void;
    inputCls?: string;
}

const PINNED = ['zh', 'en'];

export default function I18nEditor({
    value,
    onChange,
    inputCls = 'inp',
}: I18nEditorProps) {
    const [adding, setAdding] = useState('');
    const keys = sortLangKeys(
        Array.from(new Set([...PINNED, ...Object.keys(value)])),
    );
    const addable = ALL_LANG_CODES.filter((k) => !keys.includes(k));

    const set = (k: string, text: string) => onChange({ ...value, [k]: text });
    const remove = async (k: string) => {
        // 二次确认: ✕ 挨着输入框, 误触会连带丢掉该语种已填的译文
        if (
            !(await confirmDialog(
                `确认移除语种「${LANG_LABELS[k] || k}」? 该语种已填的内容会一起丢弃.`,
            ))
        )
            return;
        const next = { ...value };
        delete next[k];
        onChange(next);
    };

    return (
        <div className="space-y-1.5">
            {keys.map((k) => (
                <div key={k} className="flex items-center gap-2">
                    <span
                        className="w-24 shrink-0 text-xs text-slate-500"
                        title={k}
                    >
                        {LANG_LABELS[k] || k}
                        <span className="ml-1 font-mono text-[10px] text-slate-400">
                            {k}
                        </span>
                    </span>
                    <input
                        className={`${inputCls} flex-1`}
                        value={value[k] || ''}
                        onChange={(e) => set(k, e.target.value)}
                    />
                    {!PINNED.includes(k) && (
                        <button
                            type="button"
                            className="btn-ghost text-slate-400"
                            onClick={() => remove(k)}
                            title="移除该语种"
                        >
                            ✕
                        </button>
                    )}
                </div>
            ))}
            {addable.length > 0 && (
                <div className="flex items-center gap-2">
                    <SearchSelect
                        className="w-40 text-xs"
                        value={adding}
                        onChange={(v) => setAdding(v)}
                        placeholder="+ 添加语种…"
                        options={addable.map((k) => ({
                            value: k,
                            label: `${LANG_LABELS[k] || k} (${k})`,
                        }))}
                    />
                    <button
                        type="button"
                        className="btn-ghost"
                        disabled={!adding}
                        onClick={() => {
                            if (!adding) return;
                            set(adding, '');
                            setAdding('');
                        }}
                    >
                        添加
                    </button>
                </div>
            )}
        </div>
    );
}
